import {
  socialLinkApiMessage,
  socialLinkError,
  SOCIAL_PLATFORMS,
} from "../social";

/**
 * German wording for the messages the API sends.
 *
 * The API answers in English, because it is an interface and not a screen. The
 * admin is used by one person who does not write software, so nothing English
 * or technical may reach her. Everything the API can concretely say is
 * translated here.
 *
 * The fallback is the API's own text, not a generic sentence: if something
 * unforeseen goes wrong, a specific English message still tells her more - and
 * tells whoever she forwards it to a great deal more - than "Ein Fehler ist
 * aufgetreten".
 */

const EXACT = new Map<string, string>([
  ["Incorrect username or password.", "Benutzername oder Passwort ist falsch."],
  ["Authentication required", "Die Anmeldung ist abgelaufen."],
  ["Incorrect old password", "Das bisherige Passwort stimmt nicht."],
  ["User not found", "Dieses Benutzerkonto gibt es nicht mehr."],
  ["Password saved successfully", "Das neue Passwort ist gespeichert."],
  [
    "ids must list every picture exactly once",
    "Die neue Reihenfolge passt nicht zur Galerie. Bitte die Seite neu laden.",
  ],
  [
    'A file is required in the field "file"',
    "Bitte zuerst ein Bild auswählen.",
  ],
  [
    "pastShowsYears must be null, 1, 2 or 5",
    "Bitte eine der vier Möglichkeiten auswählen.",
  ],
  ["This video is already selected", "Dieses Video ist schon in der Liste."],
  [
    "Either youtubeId or url is required, and only one of them",
    "Bitte entweder ein Video aus dem Kanal auswählen oder einen Link einfügen.",
  ],
  [
    "That is not a link to a YouTube video",
    "Das ist kein YouTube-Link. Beispiel: https://www.youtube.com/watch?v=…",
  ],
  [
    "YouTube does not have this video",
    "Dieses Video lässt sich nicht auf der Webseite zeigen: Es ist gelöscht " +
      "oder privat, oder YouTube erlaubt das Einbetten nicht.",
  ],
  [
    "The video could not be checked with YouTube",
    "Das Video liess sich gerade nicht bei YouTube prüfen. Bitte später nochmals versuchen.",
  ],
  [
    "ids must list every video exactly once",
    "Die neue Reihenfolge passt nicht zur Liste. Bitte die Seite neu laden.",
  ],
  // These two are not a failure at all: they arrive inside a perfectly good
  // answer to the channel list, as the reason its list is empty. They belong
  // in the same table because it is the same English, and because whoever
  // reads the message should not have to know which of the two paths it took.
  [
    "The channel video list could not be loaded from YouTube",
    "Die Videoliste von YouTube lässt sich gerade nicht laden. Die ausgewählten Videos unten sind davon nicht betroffen.",
  ],
  [
    "The YouTube daily quota is used up",
    "Das Tageskontingent von YouTube ist aufgebraucht. Die Liste des Kanals ist erst morgen wieder abrufbar; die ausgewählten Videos unten sind davon nicht betroffen.",
  ],
]);

const TRACK_MESSAGES: [string, string][] = [
  [
    "That is not a link to a Spotify track, album or playlist",
    "Das ist kein Spotify-Link. Beispiel: https://open.spotify.com/track/…",
  ],
  [
    "Spotify does not have this",
    "Zu diesem Link findet Spotify nichts. Bitte den Link nochmals kopieren.",
  ],
  [
    "The link could not be checked with Spotify",
    "Der Link liess sich gerade nicht bei Spotify prüfen. Bitte später " +
      "nochmals versuchen.",
  ],
  [
    "This track is already in the list",
    "Diese Aufnahme ist schon in der Liste.",
  ],
  [
    "ids must list every track exactly once",
    "Die neue Reihenfolge passt nicht zur Liste. Bitte die Seite neu laden.",
  ],
];

for (const [english, german] of TRACK_MESSAGES) {
  EXACT.set(english, german);
}

const HIGHLIGHT_MESSAGES: [string, string][] = [
  [
    "Exactly one of picture, video, track, newVideo or newTrack is required",
    "Bitte genau ein Bild, ein Video oder eine Aufnahme auswählen.",
  ],
  ["No highlight is set", "Zurzeit ist kein Highlight gesetzt."],
  [
    "previous must be keep or delete",
    "Es ist unklar, was mit dem bisherigen Bild oder Video geschehen soll.",
  ],
];

for (const [english, german] of HIGHLIGHT_MESSAGES) {
  EXACT.set(english, german);
}

/**
 * One entry per platform, built the same way on both sides: the server answers
 * `socialLinkMessage()` from src/settings/social-links.ts, and this is the
 * German for it. Generated rather than written out five times, so a sixth
 * platform is translated the moment it is added to client/src/social.ts.
 */
for (const platform of SOCIAL_PLATFORMS) {
  EXACT.set(socialLinkApiMessage(platform.id), socialLinkError(platform.id));
}

const BY_PREFIX: [string, string][] = [
  [
    // The server spells this one out at length; its opening is enough to key
    // on, and the client asks her before it can ever be answered anyway.
    "The object the highlight leaves behind",
    "Bitte zuerst entscheiden, was mit dem bisherigen Bild oder Video " +
      "geschehen soll.",
  ],
  [
    "Missing or invalid CSRF token",
    "Die Anmeldung ist abgelaufen. Bitte erneut anmelden.",
  ],
  ["No picture with id", "Dieses Bild gibt es nicht mehr."],
  ["No show with id", "Diesen Auftritt gibt es nicht mehr."],
  ["No video with id", "Dieses Video gibt es nicht mehr."],
  ["No track with id", "Diese Aufnahme gibt es nicht mehr."],
];

export function inGerman(message: string): string {
  const exact = EXACT.get(message);
  if (exact) {
    return exact;
  }

  for (const [prefix, german] of BY_PREFIX) {
    if (message.startsWith(prefix)) {
      return german;
    }
  }

  return message;
}

/** What to say when the request never reached the server at all. */
export const OFFLINE_MESSAGE =
  "Der Server ist nicht erreichbar. Bitte die Internetverbindung prüfen.";

/** What to say when the server answered with something unreadable. */
export function unexpectedStatus(status: number): string {
  return `Unerwartete Antwort des Servers (${status}).`;
}
